import {
  ClockCircleOutlined,
  DownloadOutlined,
  PhoneFilled,
  ShareAltOutlined,
} from '@ant-design/icons';
import { Button } from 'antd';
import cn from 'classnames';
import DOMPurify from 'dompurify';
import { useEffect, useRef, useState } from 'react';

import { useLazyDownloadQuery } from '@app/api/ClientFiles';
import { AiGrade } from '@app/components/LeadCard/ui/MessageHistory/components/AiGrade';
import { LeadAudioPlayer } from '@app/components/LeadCard/ui/MessageHistory/components/LeadAudioPlayer';
import { LeadMessageTranscription } from '@app/components/LeadCard/ui/MessageHistory/components/LeadMessageTranscription';
import {
  LEAD_MSG_STATUS_DELETED,
  formatLeadMessageText,
  formatLeadMessageTimestamp,
  getLeadMessageStatusColor,
  getLeadMessageStatusIcon,
  isLeadCallRecordLoadingType,
  isLeadMissingCallType,
  parseLeadAudioDuration,
} from '@app/components/LeadCard/ui/MessageHistory/lib';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import type { NFiles } from '@app/types';
import { buildAudioDownloadName } from '@app/utils';

import type { FC } from 'react';

import './LeadCallMessage.scss';

const AUDIO_EXTENSIONS = new Set(['mp3', 'ogg', 'wav', 'amr', 'opus']);

const getAudioMimeByExt = (ext: string): string => {
  switch (ext) {
    case 'mp3':
      return 'audio/mpeg';
    case 'ogg':
    case 'opus':
      return 'audio/ogg';
    case 'wav':
      return 'audio/wav';
    case 'amr':
      return 'audio/amr';
    default:
      return 'audio/mpeg';
  }
};

interface LeadCallMessageProps {
  messageId: number;
  file: NFiles.ModelClientFile | null;
  message: string | null;
  transcription: string | null;
  messageType: string | null;
  socialType: string | null;
  isIncoming: boolean;
  timestamp: string;
  wasEdited: boolean;
  status: string | null;
  isForwarded: boolean;
  replyMessage: string | null;
  replyMessageId: number | null;
  grade: number | null;
  aiRecommendations: string | null;
}

export const LeadCallMessage: FC<LeadCallMessageProps> = ({
  messageId,
  file,
  message,
  transcription,
  messageType,
  socialType,
  isIncoming,
  timestamp,
  wasEdited,
  status,
  isForwarded,
  replyMessage,
  replyMessageId,
  grade,
  aiRecommendations,
}) => {
  const { params } = useUserPrint();
  const { showNotification } = useNotification();
  const [downloadFile] = useLazyDownloadQuery();

  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const downloadAnchorRef = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  const ext = (file?.Extension ?? '').trim().toLowerCase();
  const mime = getAudioMimeByExt(ext);
  const hasPlayableFile = !!file && AUDIO_EXTENSIONS.has(ext);
  const isMissingCall = isLeadMissingCallType(messageType);
  const isRecordLoading = isLeadCallRecordLoadingType(
    messageType,
    socialType,
    hasPlayableFile
  );
  const isDeleted = status === LEAD_MSG_STATUS_DELETED;
  const showStatusIcon = !isIncoming || isDeleted;
  const statusIcon = showStatusIcon ? getLeadMessageStatusIcon(status) : null;
  const statusColor = getLeadMessageStatusColor(status);
  const formattedTimestamp = formatLeadMessageTimestamp(timestamp);

  const title = isIncoming ? 'Входящий звонок' : 'Исходящий звонок';
  let callStateText = 'Запись звонка недоступна';

  if (isMissingCall) {
    callStateText = isIncoming ? 'Пропущенный звонок' : 'Без ответа';
  } else if (hasPlayableFile) {
    callStateText = 'Запись звонка доступна';
  } else if (isRecordLoading) {
    callStateText = 'Идёт загрузка аудиозаписи';
  }

  const sanitizedReplyHtml = replyMessage
    ? DOMPurify.sanitize(formatLeadMessageText(replyMessage, socialType))
    : null;
  const sanitizedMessageHtml = message
    ? DOMPurify.sanitize(formatLeadMessageText(message, socialType))
    : null;

  const loadAudioUrl = async (): Promise<string | null> => {
    if (audioUrl) return audioUrl;
    if (!file) return null;

    try {
      const buffer = await downloadFile({
        clientId: file.ClientId,
        fileId: file.Id,
        ActiveBranchId: params.ActiveBranchId,
      }).unwrap();

      const blob = new Blob([buffer], { type: mime });
      const url = URL.createObjectURL(blob);
      setAudioUrl(url);
      return url;
    } catch (error) {
      showNotification(error);
      return null;
    }
  };

  const handleDownload = async () => {
    setIsDownloading(true);

    try {
      const url = await loadAudioUrl();
      if (!url || !downloadAnchorRef.current) return;

      downloadAnchorRef.current.href = url;
      downloadAnchorRef.current.download = buildAudioDownloadName(
        file?.FileName,
        file?.Extension,
        `call-record-${file?.ClientId ?? 0}-${file?.Id ?? 0}`
      );
      downloadAnchorRef.current.click();
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div
      className={cn('LeadCallMessage', {
        'LeadCallMessage--out': !isIncoming,
        'LeadCallMessage--deleted': isDeleted,
        'LeadCallMessage--missed': isMissingCall && isIncoming,
        'LeadCallMessage--unanswered': isMissingCall && !isIncoming,
      })}
    >
      {isForwarded && (
        <div className="LeadCallMessage__forwarded">
          <ShareAltOutlined className="LeadCallMessage__forwarded-icon" />
          Пересланное сообщение
        </div>
      )}
      {sanitizedReplyHtml && (
        <div
          className="LeadCallMessage__reply"
          data-reply-target-id={replyMessageId ?? undefined}
        >
          <div className="LeadCallMessage__reply-indicator" />
          <div
            className="LeadCallMessage__reply-text"
            dangerouslySetInnerHTML={{ __html: sanitizedReplyHtml }}
          />
        </div>
      )}
      <div className="LeadCallMessage__main">
        <AiGrade grade={grade} aiRecommendations={aiRecommendations} />
        <div className="LeadCallMessage__icon-wrap">
          <PhoneFilled className="LeadCallMessage__icon" />
        </div>
        <div className="LeadCallMessage__summary">
          <div className="LeadCallMessage__title">{title}</div>
          <div className="LeadCallMessage__state">
            {isRecordLoading && (
              <ClockCircleOutlined className="LeadCallMessage__state-icon" />
            )}
            <span>{callStateText}</span>
          </div>
        </div>
      </div>
      {sanitizedMessageHtml && (
        <div
          className="LeadCallMessage__text"
          dangerouslySetInnerHTML={{ __html: sanitizedMessageHtml }}
        />
      )}
      {hasPlayableFile && file && (
        <LeadAudioPlayer
          fileId={file.Id}
          messageId={messageId}
          title="Запись разговора"
          isIncoming={isIncoming}
          durationSec={parseLeadAudioDuration(file.Duration)}
          onRequestUrl={loadAudioUrl}
          actions={
            <Button
              type="text"
              size="small"
              className="LeadCallMessage__download-btn"
              icon={<DownloadOutlined />}
              loading={isDownloading}
              onClick={handleDownload}
            />
          }
        />
      )}
      <LeadMessageTranscription text={transcription} />
      <a ref={downloadAnchorRef} className="LeadCallMessage__download-anchor" />
      <div className="LeadCallMessage__status">
        <span className="LeadCallMessage__timestamp">
          {wasEdited && 'изменено '}
          {formattedTimestamp}
        </span>
        {statusIcon && (
          <span
            className="LeadCallMessage__status-icon"
            style={{ color: statusColor }}
          >
            {statusIcon}
          </span>
        )}
      </div>
    </div>
  );
};
